class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.handleCartAddFormSubmit = this.handleCartAddFormSubmit.bind(this);
    document.addEventListener('submit', this.handleCartAddFormSubmit);
    this.updateShippingNote();
  }

  updateShippingNote() {
    const shippingNote = this.querySelector('[data-cart-drawer-shipping-note]');
    if (!shippingNote) return;

    const holidays = (shippingNote.dataset.dispatchHolidays || '').split(/[\s,]+/).filter(Boolean);
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        hour12: false,
        timeZone: 'Asia/Kolkata',
      })
        .formatToParts(new Date())
        .map((part) => [part.type, part.value])
    );
    const today = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)));
    const indiaHour = Number(parts.hour) % 24;
    const isDispatchDay = (date) => {
      const isoDate = date.toISOString().slice(0, 10);
      return date.getUTCDay() !== 0 && !holidays.includes(isoDate) && !holidays.includes(isoDate.slice(5));
    };

    if (indiaHour < 18 && isDispatchDay(today)) {
      shippingNote.hidden = true;
      return;
    }

    const dispatchDate = new Date(today);
    for (let step = 0; step < 14; step += 1) {
      dispatchDate.setUTCDate(dispatchDate.getUTCDate() + 1);
      if (isDispatchDay(dispatchDate)) break;
    }

    const daysAway = Math.round((dispatchDate - today) / 86400000);
    const dayLabel = shippingNote.querySelector('[data-cart-drawer-shipping-day]');
    if (dayLabel) {
      dayLabel.textContent =
        daysAway === 1
          ? 'tomorrow'
          : `on ${dispatchDate.toLocaleDateString('en-IN', {
              timeZone: 'UTC',
              ...(daysAway < 7 ? { weekday: 'long' } : { weekday: 'short', day: 'numeric', month: 'short' }),
            })}`;
    }
    shippingNote.hidden = false;
  }

  captureScrollPosition() {
    this.pageScrollPosition = {
      x: window.scrollX || window.pageXOffset || 0,
      y: window.scrollY || window.pageYOffset || 0,
    };
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    if (cartLink.tagName !== 'BUTTON') cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-controls', 'CartDrawer');
    cartLink.setAttribute('aria-expanded', 'false');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.captureScrollPosition();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (cartLink.tagName !== 'BUTTON' && event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.captureScrollPosition();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (!this.pageScrollPosition) this.captureScrollPosition();
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.updateShippingNote();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.updateHeaderCartIconExpandedState(true);
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    const scrollPosition = this.pageScrollPosition || {
      x: window.scrollX || window.pageXOffset || 0,
      y: window.scrollY || window.pageYOffset || 0,
    };

    this.classList.remove('active');
    this.updateHeaderCartIconExpandedState(false);
    removeTrapFocus();
    document.body.classList.remove('overflow-hidden');

    restoreFocusAfterModal(this.activeElement);

    window.scrollTo(scrollPosition.x, scrollPosition.y);
    requestAnimationFrame(() => window.scrollTo(scrollPosition.x, scrollPosition.y));
    this.pageScrollPosition = null;
  }

  updateHeaderCartIconExpandedState(isExpanded) {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
  }

  handleCartAddFormSubmit(event) {
    if (event.defaultPrevented) return;
    if (!(event.target instanceof HTMLFormElement)) return;
    if (!this.isCartAddForm(event.target)) return;

    event.preventDefault();
    this.submitCartAddForm(event.target, event.submitter);
  }

  isCartAddForm(form) {
    const action = form.getAttribute('action');
    if (!action || !window.routes?.cart_add_url) return false;

    const actionPath = new URL(action, window.location.origin).pathname;
    const cartAddPath = new URL(window.routes.cart_add_url, window.location.origin).pathname;

    return actionPath === cartAddPath;
  }

  showCartAddFormError(form, message = '') {
    let wrapper = form.querySelector('.product-form__error-message-wrapper');
    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.className = 'product-form__error-message-wrapper';
      wrapper.setAttribute('role', 'alert');

      const messageElement = document.createElement('span');
      messageElement.className = 'product-form__error-message';
      wrapper.appendChild(messageElement);
      form.prepend(wrapper);
    }

    wrapper.querySelector('.product-form__error-message').textContent = message;
    wrapper.toggleAttribute('hidden', !message);
  }

  setCartAddButtonLoading(submitter, loading) {
    if (!submitter) return;

    submitter.classList.toggle('loading', loading);
    if (submitter.classList.contains('gigi-add-to-cart-button')) {
      submitter.classList.toggle('is-loading', loading);
    }

    if (loading) {
      submitter.setAttribute('aria-disabled', 'true');
    } else if (!submitter.disabled) {
      submitter.removeAttribute('aria-disabled');
    }
  }

  submitCartAddForm(form, submitter) {
    if (form.dataset.gigiCartAddPending === 'true') return;
    if (submitter?.getAttribute('aria-disabled') === 'true') return;

    const formData = new FormData(form);
    const variantId = formData.get('id');
    if (!variantId) {
      this.showCartAddFormError(form, window.variantStrings?.unavailable || window.cartStrings.error);
      return;
    }

    this.captureScrollPosition();
    form.dataset.gigiCartAddPending = 'true';
    this.showCartAddFormError(form);
    this.setCartAddButtonLoading(submitter, true);

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    formData.append(
      'sections',
      this.getSectionsToRender().map((section) => section.id)
    );
    formData.append('sections_url', window.location.pathname);
    config.body = formData;
    this.setActiveElement(submitter || document.activeElement);

    return fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          const responseError = response.description || response.message;
          const errorMessage = typeof responseError === 'string' ? responseError : window.cartStrings.error;
          this.showCartAddFormError(form, errorMessage);
          return publish(PUB_SUB_EVENTS.cartError, {
            source: 'cart-drawer',
            productVariantId: variantId,
            errors: response.errors || response.description,
            message: response.message,
          });
        }

        const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
        if (this.classList.contains('is-empty')) this.classList.remove('is-empty');
        return publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer',
          productVariantId: variantId,
          cartData: response,
        }).then(() => {
          CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
          CartPerformance.measure('add:paint-updated-sections', () => {
            this.renderContents(response);
          });
        });
      })
      .catch((error) => {
        console.error(error);
        this.showCartAddFormError(form, window.cartStrings.error);
      })
      .finally(() => {
        delete form.dataset.gigiCartAddPending;
        this.setCartAddButtonLoading(submitter, false);
      });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.updateShippingNote();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
